import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radio, WifiOff } from "lucide-react";

export default function ParticipantPlay() {
  const { code: codeParam } = useParams();
  const [code, setCode] = useState((codeParam || "").toUpperCase());
  const [joined, setJoined] = useState(!!codeParam);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const poll = useRef(null);

  const fetchRoom = async (c) => {
    try {
      const { data } = await api.get(`/generator/room/${c}`);
      setRoom(data);
      setError("");
    } catch {
      setError("Room not found. Check the code with your director.");
      setRoom(null);
    }
  };

  useEffect(() => {
    if (!joined || !code) return;
    fetchRoom(code);
    poll.current = setInterval(() => fetchRoom(code), 2000);
    return () => poll.current && clearInterval(poll.current);
    // eslint-disable-next-line
  }, [joined, code]);

  if (!joined) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 stage-scrim text-white" data-testid="play-join">
        <div className="w-full max-w-sm text-center">
          <Radio className="mx-auto text-[#bf6163]" size={40} aria-hidden="true" />
          <h1 className="mt-4 font-display text-3xl">Join a live room</h1>
          <p className="mt-2 text-[#dfd8da]">Enter the 4-character code your director shows on screen.</p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
            placeholder="CODE"
            data-testid="play-code-input"
            className="mt-6 w-full h-16 text-center font-timer text-4xl tracking-[0.4em] rounded-xl text-[#1a1617]"
            maxLength={4}
          />
          <Button onClick={() => code.length === 4 && setJoined(true)} disabled={code.length !== 4}
            className="mt-4 w-full h-12 bg-[#920e12] hover:bg-[#bf6163] text-white font-bold" data-testid="play-join-btn">
            Join room
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-6 py-16 stage-scrim text-white text-center" data-testid="play-room">
      <div className="text-sm uppercase tracking-[0.3em] font-extrabold text-[#bf6163]">Room {code}</div>
      {error ? (
        <div className="mt-8" data-testid="play-error">
          <WifiOff className="mx-auto text-[#fbbf24]" size={32} />
          <p className="mt-3 text-xl text-[#dfd8da]">{error}</p>
          <Button onClick={() => { setJoined(false); }} variant="outline" className="mt-5 border-white/40 bg-transparent text-white">Change code</Button>
        </div>
      ) : room?.current ? (
        <div className="mt-6" data-testid="play-current">
          <div className="font-runner text-xl uppercase tracking-widest text-[#993538]">{room.current.category}</div>
          {room.current.scene_parts ? (
            <div className="mt-4 space-y-4">
              {room.current.scene_parts.map((sp, i) => (
                <div key={i}>
                  <div className="text-sm uppercase text-[#bf6163] font-bold">{sp.category}</div>
                  <div className="font-runner text-4xl sm:text-6xl font-black text-[#ffdedf]">{sp.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <span className="sr-only">. Prompt: </span>
              <div className="mt-4 font-runner text-5xl sm:text-7xl font-black leading-none">{room.current.text}</div>
              {room.current.paired_value && <div className="mt-6 text-2xl sm:text-4xl text-[#ffdedf] font-bold"><span className="text-[#bf6163]" aria-hidden="true">↳ </span>{room.current.paired_value}</div>}
            </>
          )}
          <div className="mt-10 text-sm text-[#dfd8da] flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#15803d] inline-block" /> Live · draw #{room.count}
          </div>
        </div>
      ) : (
        <div className="mt-8" data-testid="play-waiting">
          <p className="font-runner text-3xl text-[#dfd8da]">Waiting for the director to draw…</p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#bf6163]">
            <span className="w-2 h-2 rounded-full bg-[#bf6163] inline-block animate-pulse" /> Connected to room {code}
          </div>
        </div>
      )}
    </div>
  );
}
